import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./motion";
import { BRAND, IMAGES, SERVICES } from "../../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", phone: "", service: SERVICES[0].title, date: "", time: "", pickup: "", dropoff: "", passengers: "1", message: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.18em] text-[#94A3B8]">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const inputCls = "w-full bg-transparent border-b border-white/20 rounded-none py-3 text-[#F7F5F0] text-base placeholder-white/25 focus:outline-none focus:border-champagne transition-colors";

export const Booking = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.pickup || !form.dropoff) {
      toast.error("Merci de renseigner votre nom, téléphone, départ et destination.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
    } catch (_) { /* WhatsApp reste la voie principale */ }

    const msg =
      `Bonjour ${BRAND.name}, je souhaite réserver une course.%0A%0A` +
      `👤 Nom : ${form.name}%0A` +
      `📞 Téléphone : ${form.phone}%0A` +
      `🚗 Prestation : ${form.service}%0A` +
      `📅 Date : ${form.date || "à définir"} à ${form.time || "à définir"}%0A` +
      `📍 Départ : ${form.pickup}%0A` +
      `🏁 Destination : ${form.dropoff}%0A` +
      `👥 Passagers : ${form.passengers}%0A` +
      (form.message ? `📝 Précisions : ${form.message}%0A` : "");
    window.open(`https://wa.me/${BRAND.whatsapp}?text=${msg}`, "_blank");
    toast.success("Redirection vers WhatsApp… finalisez l'envoi de votre demande.");
    setLoading(false);
  };

  return (
    <section id="reservation" data-testid="booking" className="relative py-28 lg:py-40 bg-[#080E19] overflow-hidden">
      <div className="pointer-events-none absolute bottom-0 left-1/4 w-[700px] h-[500px] rounded-full bg-champagne/5 blur-[130px]" />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-champagne font-semibold mb-5">Réservation</p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-[#F7F5F0] leading-tight mb-6">
              Demandez votre <span className="italic text-champagne">devis</span>
            </h2>
            <p className="text-[#94A3B8] leading-relaxed mb-10 max-w-md">
              Remplissez le formulaire, votre demande sera envoyée directement sur WhatsApp. Réponse rapide et devis sur mesure, sans engagement.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="spotlight rounded-2xl border border-white/10 overflow-hidden aspect-[16/10] hidden lg:block">
              <img src={IMAGES.door} alt="Chauffeur ouvrant la portière" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form data-testid="booking-form" onSubmit={submit} className="border border-white/10 rounded-2xl p-8 lg:p-12 bg-[#0C1322]/40 grid grid-cols-1 sm:grid-cols-2 gap-8">
            <Field label="Nom complet *"><input data-testid="booking-name" className={inputCls} placeholder="Jean Dupont" value={form.name} onChange={set("name")} /></Field>
            <Field label="Téléphone *"><input data-testid="booking-phone" className={inputCls} placeholder="06 12 34 56 78" value={form.phone} onChange={set("phone")} /></Field>
            <Field label="Prestation">
              <select data-testid="booking-service" className={`${inputCls} [&>option]:text-black`} value={form.service} onChange={set("service")}>
                {SERVICES.map((s) => <option key={s.title}>{s.title}</option>)}
                <option>Autre / sur mesure</option>
              </select>
            </Field>
            <Field label="Passagers">
              <select data-testid="booking-passengers" className={`${inputCls} [&>option]:text-black`} value={form.passengers} onChange={set("passengers")}>
                {["1", "2", "3", "4", "5", "6", "7+"].map((n) => <option key={n}>{n}</option>)}
              </select>
            </Field>
            <Field label="Date"><input data-testid="booking-date" type="date" className={inputCls} value={form.date} onChange={set("date")} /></Field>
            <Field label="Heure"><input data-testid="booking-time" type="time" className={inputCls} value={form.time} onChange={set("time")} /></Field>
            <Field label="Lieu de départ *"><input data-testid="booking-pickup" className={inputCls} placeholder="Adresse, gare, aéroport…" value={form.pickup} onChange={set("pickup")} /></Field>
            <Field label="Destination *"><input data-testid="booking-dropoff" className={inputCls} placeholder="Adresse d'arrivée" value={form.dropoff} onChange={set("dropoff")} /></Field>
            <div className="sm:col-span-2">
              <Field label="Précisions"><textarea data-testid="booking-message" rows={2} className={inputCls} placeholder="Bagages, siège enfant, arrêts…" value={form.message} onChange={set("message")} /></Field>
            </div>
            <div className="sm:col-span-2">
              <motion.button whileTap={{ scale: 0.97 }} data-testid="booking-submit" type="submit" disabled={loading} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-champagne text-[#060B14] px-10 py-4 font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
                {loading ? <Loader2 className="animate-spin" size={18} /> : <MessageCircle size={18} />}
                Envoyer via WhatsApp
              </motion.button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
